import React, { ChangeEvent, FormEvent, useState } from "react";

import { showToast } from "../Toast";
import { useLoginSession } from "../../logic/useLoginSession";
import { LoginData } from "../../types/auth.types";

interface LoginFormProps {
  onSwitchToRegister: () => void;
  onSwitchToForgotPassword: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({
  onSwitchToRegister,
  onSwitchToForgotPassword,
}) => {
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const [form, setForm] = useState<LoginData>({
    username: "",
    password: "",
  });

  const { signIn } = useLoginSession();

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };

  const validate = () => {
    const errors: { [key: string]: string } = {};
    if (!form.username.trim()) errors.username = "Username is required.";
    if (!form.password.trim()) errors.password = "Password is required.";
    return errors;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setIsSaving(true);

    try {
      // signIn writes the session, finishes any saved wishlist/cart/Buy Now
      // click, closes this modal, and navigates only if a real page redirect
      // was saved.
      const result = await signIn(form);
      if (!result.ok) {
        showToast({ type: "error", message: result.message });
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="auth-form-wrapper animate-fade-in">
      <div className="mb-4">
        <h2 className="auth-title">Login</h2>
        <p className="auth-subtitle">Enter your details to sign in to your account</p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Username */}
        <div className="modern-input-group">
          <label className="modern-label">
            Username <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            name="username"
            className={`modern-input ${errors.username ? "is-invalid" : ""}`}
            placeholder="Enter your username"
            value={form.username}
            onChange={handleChange}
            disabled={isSaving}
          />
          {errors.username && (
            <div className="invalid-feedback d-block mt-1">{errors.username}</div>
          )}
        </div>

        {/* Password */}
        <div className="modern-input-group">
          <label className="modern-label">
            Password <span className="text-danger">*</span>
          </label>
          <div className="input-with-icon">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              className={`modern-input ${errors.password ? "is-invalid" : ""}`}
              placeholder="Enter your password"
              value={form.password}
              onChange={handleChange}
              disabled={isSaving}
            />
            <button
              type="button"
              className="input-icon-btn"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              {showPassword ? (
                <i className="bi bi-eye-slash-fill fs-5"></i>
              ) : (
                <i className="bi bi-eye-fill fs-5"></i>
              )}
            </button>
          </div>
          {errors.password && (
            <div className="invalid-feedback d-block mt-1">{errors.password}</div>
          )}
        </div>

        {/* Submit */}
        <button type="submit" className="modern-btn mt-4" disabled={isSaving}>
          {isSaving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Signing In...
            </>
          ) : (
            "Sign In"
          )}
        </button>
      </form>

      {/* Links */}
      <div className="auth-footer">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            onSwitchToRegister();
          }}
          className="auth-link"
        >
          I don't have an account
        </a>

        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            onSwitchToForgotPassword();
          }}
          className="auth-link auth-link-small text-muted fw-normal"
        >
          Forget Password?
        </a>
      </div>
    </div>
  );
};

export default LoginForm;
